import { useTranslation } from 'react-i18next';
import { Link, useLocation } from '@tanstack/react-router';

export interface SidebarProps {
  /** モバイルでの表示状態 */
  isOpen?: boolean;
  /** 閉じる処理 */
  onClose?: () => void;
}

interface NavItem {
  key: string;
  path: string;
  icon: React.ReactNode;
}

/** @graph-connects none */
const resolveNavItem: NavItem = {
  key: 'resolve',
  path: '/',
  icon: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M21 21l-4.35-4.35m1.85-5.4a7.5 7.5 0 11-15 0 7.5 7.5 0 0115 0z"
      />
    </svg>
  ),
};

/** @graph-connects none */
const profileNavItem: NavItem = {
  key: 'profile',
  path: '/profile',
  icon: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
      />
    </svg>
  ),
};

/** @graph-connects none */
const navItems: NavItem[] = [resolveNavItem, profileNavItem];

/**
 * @graph-stack line-user-id-resolver
 * @graph-domain Engineering
 * @graph-business サイドバーナビゲーション。resolve 機能のみへのリンクをガラスカード背景で提供。モバイルスライドイン・デスクトップ固定の2モード対応
 * @graph-connects none
 */
export function Sidebar({ isOpen = false, onClose }: SidebarProps) {
  const { t } = useTranslation();
  const location = useLocation();

  const isActive = (path: string) =>
    path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);

  const sidebarContent = (
    <nav className="flex flex-col h-full" aria-label="Main navigation">
      <div className="flex-1 py-4 space-y-1">
        {navItems.map((item) => (
          <Link
            key={item.key}
            to={item.path}
            onClick={onClose}
            className={`
              flex items-center gap-3 px-4 py-2.5 mx-2 rounded-lg
              text-sm font-medium transition-colors
              ${
                isActive(item.path)
                  ? 'bg-[var(--color-accent-muted)] text-[var(--color-accent-primary)]'
                  : 'text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-bg-hover)]'
              }
            `}
            aria-current={isActive(item.path) ? 'page' : undefined}
          >
            {item.icon}
            <span>{t(`nav.${item.key}`)}</span>
          </Link>
        ))}
      </div>

      <div className="p-4 border-t border-[var(--color-border)]">
        <p className="text-xs text-[var(--color-text-muted)] text-center">{t('app.name')}</p>
      </div>
    </nav>
  );

  return (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 md:hidden"
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      <aside
        className={`
          fixed top-0 left-0 z-50 h-full w-64
          bg-[var(--color-bg-surface)] border-r border-[var(--color-border)]
          transform transition-transform duration-300 ease-in-out
          md:hidden
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        <div className="flex items-center justify-between h-14 px-4 border-b border-[var(--color-border)]">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-[var(--color-accent-primary)] flex items-center justify-center">
              <span className="text-white font-bold text-sm">LR</span>
            </div>
            <span className="font-semibold gradient-text">{t('app.name')}</span>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-bg-hover)] transition-colors"
            aria-label={t('common.close')}
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
        {sidebarContent}
      </aside>

      <aside className="hidden md:flex md:flex-col md:w-64 md:flex-shrink-0 glass-card border-r border-[var(--glass-border)] rounded-none">
        {sidebarContent}
      </aside>
    </>
  );
}
